# Application complete signal

The application complete signal signifies that a job application has been submitted in your recruitment software.
It applies to positions that are either advertised on SEEK or have the Apply with SEEK button enabled.
Note that it does _not_ imply a candidate source or Apply with SEEK usage;
[Ad Performance] analytics will only attribute completed applications where the candidate source is SEEK.

[Ad Performance]: /use-cases/ad-performance

import { Alert, Text } from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

<Alert tone="info">
  <Text>
    If you are implementing Apply with SEEK, refer to{' '}
    <SmartTextLink href="/use-cases/apply-with-seek">
      its documentation
    </SmartTextLink>{' '}
    as your implementation of the application complete signal will differ from
    the below.
  </Text>
</Alert>

Once a candidate has completed and submitted your apply form,
your software must send an application complete signal to SEEK to signify form submission.
The application complete signal requires a `seek-token` that your software received from the [link out] flow.

The application complete signal is only required if your software supports posting job ads that [link out] to an external apply form.

[link out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form

```mermaid Application complete signal flow
sequenceDiagram
  autonumber
  participant Candidate as Candidate
  participant SEEK as SEEK
  participant Partner as Partner
  participant SEEKAPI as SEEK API

  Candidate->>SEEK: Click "Apply"
  SEEK-->>Candidate: Redirect to partner with token
  Candidate->>Partner: Complete apply form
  Partner->>SEEKAPI: Send complete signal with token
```

## Receiving the token

You will receive the token in a `seek-token` parameter appended to the query string of the posted [`applicationUri`](/schema/named-type/ApplicationMethod#/field/applicationUri).

```text
https://example.com/position/123/apply?seek-token=eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9
```

The token is unique to that specific candidate, job ad and session, and should be stored for the duration of the application process.

### Token lifecycle

Tokens remain valid for 180 days from the time of generation.
If your software allows candidates to save a draft application, the token must be stored with the draft and used when the candidate submits the application.

Should an expired token be provided to the `sendSignal` mutation, the SEEK API will internally handle the error and no error message will be returned.

When a candidate completes an application, the token must be stored with their application for potential future use.

If a candidate returns to an apply form via the same SEEK link-out URL,
you will receive a new `seek-token`.
This should override any previous token for the job application when sending subsequent signals to SEEK.

## Sending the application complete signal

To send the application complete signal,
use the [`sendSignal` mutation], passing the `seek-token` as the [`token` argument].
The optional `timestamp` field can be provided to indicate when the application was completed by the candidate, see [Sending signals] for details.

```graphql
mutation ($input: SendSignalInput!) {
  sendSignal(input: $input)
}
```

```jsonc
{
  "input": {
    // Details of the type of signal being sent
    "typeCode": "ApplicationComplete",

    // The token received from SEEK as part of the link-out URL
    "token": "globalPublicTest-WfRFAhG25YC8ADe3QvxAVz",
    "timestamp": "2020-01-02T00:00:00.000Z"
  }
}
```

```json
{
  "sendSignal": true
}
```

[`sendSignal` mutation]: /schema/mutation/sendSignal
[`token` argument]: /schema/mutation/sendSignal#/argument/token
[Sending signals]: /use-cases/selection-signals/sending-signals

## Error handling

[`sendSignal`] will return errors consistent with our documented [GraphQL error responses].
If your software receives authentication or transient server errors, it must retry sending of application complete signals as described in the documentation.

We recommend that you log full requests and responses to the [`sendSignal`] mutation during development to facilitate debugging.
Use a unique [`X-Request-Id`] for each request to assist in support investigations.

### Troubleshooting

Below are common errors that may be returned when sending an application complete signal.
Refer to our [GraphQL error responses] for full details of errors returned from the SEEK API.

| `extensions.code`   | `message`                                                      | Description                                                                                                                       | Resolution                                                                                                                  |
| :------------------ | :------------------------------------------------------------- | :-------------------------------------------------------------------------------------------------------------------------------- | :-------------------------------------------------------------------------------------------------------------------------- |
| [`BAD_USER_INPUT`]  | `/input/token: Is not valid`                                   | The request has an `/input/token` that is not recognised.                                                                         | Ensure the token sent in [`sendSignal`] exactly matches a `seek-token` received with no additional encoding nor whitespace. |
| [`UNAUTHENTICATED`] | `Authorization header missing`                                 | The request does not have an `Authorization` header.                                                                              | Ensure a [partner token] is passed in the `Authorization` header.                                                           |
| [`UNAUTHENTICATED`] | `Live tokens are not accepted when accessing public test data` | The request has a production `Authorization` header but its `/input/token` was taken from our documentation for [Playground] use. | Send real `seek-token`s received from the [link out] flow.                                                                  |

[`BAD_USER_INPUT`]: /graphql/error-responses#bad_user_input
[`sendSignal`]: /schema/mutation/sendSignal
[`UNAUTHENTICATED`]: /graphql/error-responses#unauthenticated
[`X-Request-Id`]: /graphql/in-practice#tracing-requests
[GraphQL error responses]: /graphql/error-responses
[partner token]: /auth/partner-tokens
[Playground]: /graphql/playground
